import { useState } from 'react';
import { api } from '../lib/api.js';
import { downscaleImage } from '../lib/downscale-image.js';

// ─── Flag / Report Issue Modal ────────────────────────────────────────────────
export function FlagModal({ task, locationId, userId, onClose, onSubmit }) {
  const [desc, setDesc] = useState('');
  const [photoData, setPhotoData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // The photo is shrunk before it is attached (CJ 2026-10-06, from Lili at
  // Bristol: reporting "wasn't working"). A phone camera photo went up at full
  // size, the server refused it, and nothing here said so.
  async function handleFileChange(e) {
    const file = e.target.files[0];
    setError('');
    if (!file) { setPhotoData(null); return; }
    try { setPhotoData((await downscaleImage(file)).base64); }
    catch { setPhotoData(null); setError('Could not read that photo. Try another, or send the report without one.'); }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!desc.trim()) return;
    setSaving(true); setError('');
    try {
      await api('POST', '/api/flags', {
        task_completion_id: task.completion_id,
        task_id: task.id, location_id: locationId,
        flagged_by: userId, description: desc.trim(),
        photo_url: photoData || null,
      });
      onSubmit();
    } catch (err) {
      let m = err?.message || 'unknown error';
      try { m = JSON.parse(m).error || m; } catch { /* not JSON */ }
      setError(`The report was not sent: ${m}`);
    } finally { setSaving(false); }
  }

  // iOS Safari is finicky with flex:1 on <form> directly — the form
  // sometimes ignores the constraint and overflows. Splitting the
  // scrollable area into a regular <div> keeps the form's submit
  // semantics but lets the wrapper own the height + overflow.
  return (
    <form onSubmit={handleSubmit}
      className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl flex flex-col fade-in shadow-2xl"
           style={{ maxHeight: '85vh', height: 'auto' }}
           onClick={e => e.stopPropagation()}>
        <div className="bg-red-600 rounded-t-2xl p-5 flex items-center justify-between flex-shrink-0">
          <div>
            <p className="text-red-200 text-xs uppercase tracking-wider mb-0.5">Report Issue</p>
            <h3 className="text-white font-bold text-base leading-tight">{task.title}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-white/60 hover:text-white text-3xl leading-none ml-4">×</button>
        </div>
        {/* Scrollable body — div wrapper instead of <form> so iOS computes
            flex height correctly. */}
        <div className="p-5 space-y-4"
             style={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch', flex: 1, minHeight: 0 }}>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Describe the Issue *</label>
            <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={3} required
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-ink bg-white focus:outline-none focus:ring-2 focus:ring-red-300 resize-none"
              placeholder="e.g. Task was marked complete but the espresso machine was not actually backflushed..." />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Photo (optional)</label>
            <input type="file" accept="image/*" capture="environment" onChange={handleFileChange}
              className="w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-red-50 file:text-red-600 hover:file:bg-red-100 cursor-pointer" />
            {photoData && (
              <img src={photoData} alt="Preview" className="mt-2 w-20 h-16 object-cover rounded-lg border border-gray-200" />
            )}
          </div>
        </div>
        {error && <div role="alert" className="px-5 pb-2 text-sm text-red-700">{error}</div>}
        {/* Footer pinned outside the scroll area so Cancel/Submit are
            always reachable even on tiny screens. */}
        <div className="p-5 pt-3 flex gap-3 flex-shrink-0 border-t border-gray-100">
          <button type="button" onClick={onClose} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-semibold hover:bg-gray-50 text-sm">
            Cancel
          </button>
          <button type="submit" disabled={saving || !desc.trim()}
            className="flex-1 py-3 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-50 transition-all text-sm">
            {saving ? 'Submitting...' : 'Submit Report'}
          </button>
        </div>
      </div>
    </form>
  );
}
